import React, { useCallback } from 'react';
import { Grid } from '~/components/';
import { useForm } from '~/hooks';
import { MdAddCircle } from 'react-icons/md';
import { FaPencilAlt, FaTrash} from 'react-icons/fa';
import AddOwner from './AddOwner';
import { useSectionLoader, useSaveSectionDetail, useDeleteSectionDetail } from '../../hooks';
import sectionTypes from '../sectionTypes';


/**
 * @typedef CurrentOwnersValues
 * @property {number}   sectionId
 * @property {string}   titleOpinionId 
 * @property {object}   ownerType 
 * @property {object}   ownerPrimary 
 * @property {object}   ownerSecondary 
 * @property {[object]} ownerTypeOptions 
 * @property {[object]} sectionDetails
 * @property {object}   relationship
 * @property {[object]} relationshipOptions
 * @property {boolean}  addOwnerFlag
 * 
 * 
 * @typedef FormState
 * @property {CurrentOwnersValues} values The current form values
 */

/** @type {CurrentOwnersValues} */

const defaultState = {
    id: 0,
    sectionId: 0,
    ownerType: { label: '...', value: '' },
    ownerTypeOptions: [],
    relationship: { label: '...', value: '' },
    relationshipOptions: [],
    sectionDetails: [],
    data: [],
    addOwnerModalFLag: false,
    ownerDetailData: {},
    editOwner: false,
    sectionType: sectionTypes.PROPERTY_INFO
};

const loadData = async (values) => {
    const defaultOwnerType = defaultState.ownerTypeOptions.find(s => s.id == 1);
    return {
        ...values,
        sectionId: values ? values.sectionId: 0,
        ownerPrimaryType: values? values.ownerPrimaryType : defaultOwnerType,
        ownerSecondaryType: values? values.ownerSecondaryType :  defaultOwnerType
    };
};

const CurrentOwners = (props) => {
    const { onLoad: loadSection } = useSectionLoader(sectionTypes.PROPERTY_INFO, props.titleOpinionId);

    const onLoad = useCallback((values) => {
        return loadData(values).then(loadSection(values));
    }, [props.sectionDetails]);

    const ownerDetail =  (props.sectionDetails && Object.keys(props.sectionDetails).length > 0) ? props.sectionDetails.map((detail) => {
        
        const primaryOwnerName = detail._data.ownerPrimaryType.value == 'Individual' ?
            (Object.values(detail._data.ownerPrimary).join(' '))
            : detail._data.ownerPrimary.ownerEntityName + ', ' + detail._data.ownerPrimary.ownerEntityType.label;

        const secondaryOwnerName = detail._data.ownerSecondary && Object.keys(detail._data.ownerSecondary).length > 0 ?
            detail._data.ownerSecondaryType.value == 'Individual' ?
                (Object.values(detail._data.ownerSecondary).join(' '))
                : detail._data.ownerSecondary.ownerEntityName + ', ' + detail._data.ownerSecondary.ownerEntityType.label
            :
            null;

        const ownerName = secondaryOwnerName ? primaryOwnerName + ' ' + detail._data.relationship.label + ' ' + secondaryOwnerName : (primaryOwnerName + ' ' + detail._data.relationship.label);
        
        return {
            ...detail,
            id: detail.sectionDetailId,
            ownerName: ownerName            
        } ;
    }) : [];

    const { state, fieldActions } = useForm({
        initialState: {...defaultState},
        onLoad,
        debug: true 
    });    

    const { deleteSectionDetail } = useDeleteSectionDetail(state.values.data, state.values.sectionDetailId);

    const onEditClick = async (data) => {
        await fieldActions.onChange(data, 'ownerDetailData');
        await fieldActions.onChange(true, 'editOwner');
        fieldActions.onChange(true, 'addOwnerModalFLag');
    };

    const onDeleteClick = (id) => {
        deleteSectionDetail(id);
    };

    const addOwnerModal = () => {
        fieldActions.onChange(false, 'editOwner');
        fieldActions.onChange(true, 'addOwnerModalFLag');
    };

    const cancelAddOwner = () => {
        fieldActions.onChange(false, 'addOwnerModalFLag');
    }; 

    const { saveField } = useSaveSectionDetail(state.values.data, state.values.sectionType, props.initialDefaultData, props.titleOpinionId, state.values.sectionDetailId);
    
    const addOwner = async (values) => {

        const data = {
            sectionId: props.sectionId,
            type: 'owner',
            ownerPrimaryType: values.ownerPrimaryType,
            ownerPrimary: values.ownerPrimary,
            relationship: values.relationship,
            ownerSecondaryType: values.ownerSecondaryType,
            ownerSecondary: values.ownerSecondary            
        };

        await fieldActions.onChange(data, 'data');
        await saveField( state.values.data, state.values.sectionType, props.titleOpinionId, state.values.sectionDetailId);
        await fieldActions.onChange(false, 'addOwnerModalFLag');
        
    };

    const saveOwner = async (prevData, newData) => {

        const data = {
            sectionId: props.sectionId,
            sectionDetailId: prevData.sectionDetailId,
            type: 'owner',
            ownerPrimaryType: newData.ownerPrimaryType,
            ownerPrimary: newData.ownerPrimary ? newData.ownerPrimary : prevData._data.ownerPrimary,
            relationship: newData.relationship,
            ownerSecondaryType: newData.ownerSecondaryType,
            ownerSecondary: newData.ownerSecondary ? newData.ownerSecondary : prevData._data.ownerSecondary            
        };
        
        await fieldActions.onChange(data, 'data');
        await saveField( state.values.data, state.values.sectionType, props.initialDefaultData, props.titleOpinionId, data.sectionDetailId);
        fieldActions.onChange(false, 'addOwnerModalFLag');
    };

    const columns = [
        {
            title: "Owner Name",
            field: 'ownerName',
            width: 500
        },
        {
            field: 'editDelete',
            width: 150,
            canSort: false,
            cell: ({ data }) => {
                return (
                    <>
                        {!props.editMode ? <span className="h-10" /> :
                            <span className="flex justify-end h-10">
                                <button className="btn btn-link mr-5" 
                                    onClick={() => onEditClick(data)}>
                                    <FaPencilAlt />
                                </button>
                                <button className="btn btn-link" 
                                    onClick={() => onDeleteClick(data.id)}>
                                    <FaTrash />
                                </button>
                            </span>
                        }                        
                    </>
                    
                );
            }
        }
    ];

    return (
        <React.Fragment>
            <div className="col-span-12 mt-2">
                <Grid
                    className= "grid-stretch -mb-2"
                    columns={columns}
                    data={ownerDetail}
                />

                <AddOwner
                    isModalOpen={state.values.addOwnerModalFLag}
                    ownerDetailData={state.values.ownerDetailData}
                    editOwner={state.values.editOwner}
                    onCancel={() => cancelAddOwner()}
                    headerTxt={'Add Owner'}
                    sectionId={props.sectionId}
                    addOwner={(values) => addOwner(values)}
                    saveOwner={(a, b) => saveOwner(a, b)}
                />
            </div>
            {!props.editMode ?  <span className='col-span-12 mb-8' /> : 
                <div className= 'col-span-12 mb-8'>
                    <div className="flex justify-start items-center pt-0 mt-auto">
                        <button
                            className='py-1.5 px-2 rounded-full text-white flex items-center focus:outline-none bg-blue-60 disabled:opacity-50'
                            onClick={() => addOwnerModal()}
                        >
                            <MdAddCircle className="mr-2" size="1.25rem" />
                            Add Owner
                        </button>
                    </div>
                </div>
            }
            
        </React.Fragment>
    );
};

export default CurrentOwners;